/**
 * index.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
import "@babel/polyfill";

// Import all the third party stuff
import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { ConnectedRouter } from "connected-react-router";
import { BrandProvider } from "contexts/brand";

import history from "utils/history";
import { googleAnalyticsInit } from "utils/tracking/google-analytics";
import { initGoogleTagManager } from "utils/tracking/google-tag-manager";
import initPageViewTracking from "utils/tracking/page-view-tracker";
import "sanitize.css/sanitize.css";

// Import root app
import App from "containers/app";
import { REACT_APP_SENTRY_DSN } from "./config";

// Import i18n messages
import { translationMessages } from "./i18n";
import "./styles/global.scss";
// Create redux store with history
import configureStore from "./configure-store";
import * as Sentry from "@sentry/react";

// CSS custom properties are now set by brand.js (loaded before React)

const store = configureStore();
const MOUNT_NODE = document.getElementById("app");

// Initialize all tracking services
googleAnalyticsInit();
initGoogleTagManager();

// Initialize page view tracking with history
initPageViewTracking(history);

Sentry.init({
  dsn: REACT_APP_SENTRY_DSN,
});

const root = ReactDOM.createRoot(MOUNT_NODE);

const render = (messages) => {
  root.render(
    <Provider store={store}>
      <BrandProvider>
        <ConnectedRouter history={history}>
          <App />
        </ConnectedRouter>
      </BrandProvider>
    </Provider>,
  );
};

if (module.hot) {
  // Hot reloadable React components and translation json files
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  module.hot.accept(["./i18n", "containers/app"], () => {
    render(translationMessages);
  });
}

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  new Promise((resolve) => {
    resolve(import("intl"));
  })
    .then(() => Promise.all([import("intl/locale-data/jsonp/en.js")]))
    .then(() => render(translationMessages))
    .catch((err) => {
      throw err;
    });
} else {
  render(translationMessages);
}

// Install ServiceWorker and AppCache in the end since
// it's not most important operation and if main code fails,
// we do not want it installed
if (process.env.NODE_ENV === "production") {
  require("offline-plugin/runtime").install(); // eslint-disable-line global-require
}
